import { gsap } from "gsap";
import { Container } from "pixi.js";

import { IReelData } from "../../../abstracts/elements/AbstractReel";
import { EventsManager } from "../../../abstracts/events/EventsManager";
import { AbstractGameMode } from "../../../abstracts/flow/AbstractGameMode";
import { TimelineLabels } from "../../../abstracts/types/enums";
import { IScatterDecoratorData } from "../../../abstracts/types/interfaces";
import { isAllTrue } from "../../../abstracts/utils/ArrayUtils";
import { playSound, stopSound } from "../../../abstracts/utils/SoundUtils";
import { Apocalypse } from "../../Apocalypse";
import {
    ApocalypseSoundNames,
    scatterAnticipationLandingSounds,
    scatterLandingSounds,
} from "../../assetsLoader/ApocalypseAssetsNames";
import { davidPanningReelStop, reelsConfig } from "../../config/ApocalypseConfig";
import {
    REMOVE_FAKE_SCATTERS,
    RESET_FAKE_SCATTERS,
    SHAKE_NORMAL_SCREEN,
} from "../../events/ApocalypseEvents";
import { ApocalypseNormalContainer } from "../../modules/Reels/normal/ApocalypseNormalContainer";
import ApocalypseReel from "../../modules/Reels/normal/ApocalypseReel";
import { ApocalypseScatterSymbol } from "../../modules/Reels/symbols/ApocalypseScatterSymbol";
import { ApocalypseSpecialSymbolNames, ScatterStates } from "../../types/ApocalypseEnums";

export const ApocalypseResultDecorator = (data: IScatterDecoratorData): void => {
    const {
        scatterSymbolName = `scatter`,
        finalDataReels = [],
        gameReels = [],
        restrictedReelsByIndex = [],
        timeline = gsap.timeline(),
        stopSpinDelay = 0,
        nearWinDuration = 0.5,
        wasFeatureBuy = false,
        minScattersToTriggerFeature = 3,
        onDecoratorComplete = () => {},
        gameMode = AbstractGameMode.NORMAL,
        reelsContainer = new Container(),
    } = data;

    const normalContainer = reelsContainer as ApocalypseNormalContainer;
    const minScattersToTriggerNearWin: number = minScattersToTriggerFeature - 1;

    let scattersCounter: number = 0;

    let nearWinOnReels: boolean[] = [];
    let hasSXNudgeNearWin: boolean = false;
    let sxnPosition: number = -1;

    let allowLuckyDrawAnticipation: boolean = false;

    finalDataReels.forEach((symbols: string[], reelIndex: number) => {
        let hasNearWin: boolean = false;

        // if (scattersCounter === minScattersToTriggerNearWin) {
        if (scattersCounter >= minScattersToTriggerNearWin) {
            if (!restrictedReelsByIndex.includes(reelIndex)) {
                hasNearWin = true;
            }

            if (scattersCounter === 3) {
                allowLuckyDrawAnticipation = true;
            }
        }

        let symbolsIncludeScatter: boolean = false;
        symbols.forEach((symbolName: string) => {
            if (symbolName === scatterSymbolName) {
                symbolsIncludeScatter = true;
            }
        });

        if (symbolsIncludeScatter) {
            scattersCounter++;
        }

        nearWinOnReels[reelIndex] =
            hasNearWin &&
            gameMode === AbstractGameMode.NORMAL &&
            (!wasFeatureBuy ||
                (Apocalypse.model.isLuckyDraw && allowLuckyDrawAnticipation) ||
                Apocalypse.model.isXbet ||
                Apocalypse.model.isSXNBet);

        if (
            reelIndex === 3 &&
            finalDataReels[2].includes(ApocalypseSpecialSymbolNames.WILD) &&
            !Apocalypse.model.isSXNBet
        ) {
            hasSXNudgeNearWin = true;

            nearWinOnReels[reelIndex] = true;
            sxnPosition = finalDataReels[2].indexOf(ApocalypseSpecialSymbolNames.WILD);
        }
    });

    timeline.addLabel(TimelineLabels.SCATTER_DECORATOR_START, `>`);

    const stoppedReels: boolean[] = new Array(finalDataReels.length).fill(false);

    const onReelSpinComplete = (reelId: number): void => {
        stoppedReels[reelId] = true;

        if (reelId === 3) {
            stopSound({ name: ApocalypseSoundNames.WILD_LAND_1 });
        }

        if (
            reelId === reelsConfig.reelsNumber - 1 ||
            (reelId > 2 && (nearWinOnReels[reelId - 1] || nearWinOnReels[reelId - 2]))
        ) {
            stopSound({
                name: ApocalypseSoundNames.SCATTER_FRICTION,
            });
        }
        if (isAllTrue(stoppedReels)) {
            const revealUnlockedSymbolsTimeline: gsap.core.Timeline = gsap.timeline({
                onComplete: () => {
                    scatterAnticipationLandingSounds.forEach((soundName: ApocalypseSoundNames) => {
                        stopSound({ name: soundName });
                    });
                    // console.log(`tlDuration`, tlDuration);
                    if (Apocalypse.model.data.nextMode === AbstractGameMode.NORMAL) {
                        EventsManager.trigger(RESET_FAKE_SCATTERS);
                    }
                    if (winningScatters.length <= minScattersToTriggerNearWin) {
                        EventsManager.trigger(REMOVE_FAKE_SCATTERS);
                    }
                    onDecoratorComplete();
                },
            });
            const revealTopBlockersTimeline: gsap.core.Timeline = gsap.timeline();
            revealTopBlockersTimeline.data = { name: `revealTopBlockersTimeline` };
            const revealBottomBlockersTimeline: gsap.core.Timeline = gsap.timeline();
            revealBottomBlockersTimeline.data = { name: `revealBottomBlockersTimeline` };
        }
    };

    let firstNearWinToStop: boolean = true;
    // shuffleArray(scatterAnticipationLandingSounds);
    let currentAnticipationLandSound = 0;
    let reelStopTimeline: gsap.core.Timeline = gsap.timeline({
        autoRemoveChildren: true,
        smoothChildTiming: true,
    });

    let totalScattersStoppedOnLanding: number = 0;
    let winningScatters: ApocalypseScatterSymbol[] = [];
    let scatterState: ScatterStates = ScatterStates.NORMAL;
    let totalReelsLanded: number = 0;

    const onReelLand = (reelId: number): void => {
        totalReelsLanded++;

        if (reelId === 3) {
            if (Apocalypse.model.hasWildsToNudge) {
                EventsManager.trigger(SHAKE_NORMAL_SCREEN, 6);
                playSound({ name: ApocalypseSoundNames.WILD_LAND_2 });
            } else {
                if (finalDataReels[2].includes(ApocalypseSpecialSymbolNames.WILD)) {
                    playSound({ name: ApocalypseSoundNames.WILD_MISS });
                }
            }
        }

        if (currentAnticipationLandSound >= 1) {
            stopSound({
                name: scatterAnticipationLandingSounds[currentAnticipationLandSound - 1],
            });
        }

        if (
            nearWinOnReels[reelId] &&
            !finalDataReels[2].includes(ApocalypseSpecialSymbolNames.WILD)
        ) {
            playSound({
                name: scatterAnticipationLandingSounds[currentAnticipationLandSound],
            });
            currentAnticipationLandSound++;
        }

        if (finalDataReels[reelId].toString().includes(scatterSymbolName)) {
            totalScattersStoppedOnLanding++;

            // if (gameMode !== AbstractGameMode.NORMAL) {
            //     // const openedPositionsLength: number = 0;
            //     // Apocalypse.model.data.extraPositionsOpen.filter(
            //     //     (isOpen) => isOpen === true
            //     // ).length;
            //     // totalScattersStoppedOnLanding +=
            //     //     openedPositionsLength - totalScattersStoppedOnLanding;
            //     // playSound({
            //     //     name: scatterLandingSounds[totalScattersStoppedOnLanding - 1],
            //     //     stereo: davidPanningReelStop[totalScattersStoppedOnLanding - 1],
            //     // });
            // } else {
            // if (
            //     totalScattersStoppedOnLanding === minScattersToTriggerNearWin &&
            //     reelId === finalDataReels.length - 1
            // ) {
            //     if (Apocalypse.model.isXbet) {
            //         // playSound({ name: ApocalypseSoundNames.SCATTER_LANDING_1_XBET });
            //     } else {
            //         // playSound({ name: ApocalypseSoundNames.SCATTER_LANDING_2 });
            //     }
            //     totalScattersStoppedOnLanding -= 1;
            // } else {
            // if (reelId + 1 < 5) {
            if (
                totalScattersStoppedOnLanding === minScattersToTriggerNearWin &&
                (!wasFeatureBuy || Apocalypse.model.isLuckyDraw) &&
                reelId !== reelsConfig.reelsNumber - 1
            ) {
                playSound({
                    name: ApocalypseSoundNames.SCATTER_ANTICIPATION,
                });
                playSound({
                    name: ApocalypseSoundNames.SCATTER_FRICTION,
                });
            }

            // if (gameMode === AbstractGameMode.NORMAL) {
            // if (reelId === 1) {
            //     if (totalScattersStoppedOnLanding === 1) {
            //         if (Apocalypse.model.isXbet) {
            //             // playSound({
            //             //     name: ApocalypseSoundNames.SCATTER_LANDING_1_XBET,
            //             //     stereo: davidPanningReelStop[totalScattersStoppedOnLanding - 1],
            //             // });
            //         } else {
            //             // playSound({
            //             //     name: ApocalypseSoundNames.SCATTER_LANDING_1,
            //             //     stereo: davidPanningReelStop[totalScattersStoppedOnLanding - 1],
            //             // });
            //         }
            //     } else {
            //         // playSound({
            //         //     name: scatterLandingSounds[totalScattersStoppedOnLanding - 1],
            //         //     stereo: davidPanningReelStop[totalScattersStoppedOnLanding - 1],
            //         // });
            //     }
            // } else {
            if (
                wasFeatureBuy &&
                !Apocalypse.model.isLuckyDraw &&
                totalScattersStoppedOnLanding > 2
            ) {
                stopSound({
                    name: scatterLandingSounds[totalScattersStoppedOnLanding - 2],
                });
            }

            if (
                !(
                    reelId === reelsConfig.reelsNumber - 1 &&
                    totalScattersStoppedOnLanding <= minScattersToTriggerNearWin
                )
            ) {
                playSound({
                    name: scatterLandingSounds[totalScattersStoppedOnLanding - 1],
                    stereo: davidPanningReelStop[totalScattersStoppedOnLanding - 1],
                });
            }

            // }
            // }
            // }
        }

        if (
            reelId === finalDataReels.length - 1 &&
            totalScattersStoppedOnLanding <= minScattersToTriggerNearWin
        ) {
            if (totalScattersStoppedOnLanding === minScattersToTriggerNearWin) {
                playSound({ name: ApocalypseSoundNames.SCATTER_ANTICIPATION_FAIL });
            }
        }

        if (
            reelId === finalDataReels.length - 1 &&
            totalScattersStoppedOnLanding > minScattersToTriggerNearWin &&
            (!wasFeatureBuy || Apocalypse.model.isLuckyDraw)
        ) {
            playSound({ name: ApocalypseSoundNames.SCATTER_ANTICIPATION_WIN });
        }

        //////////////////handle scatters win animation
        if (finalDataReels[reelId].toString().includes(scatterSymbolName)) {
            const scatterSymbol = normalContainer.fakeScatters[reelId];
            if (scatterSymbol !== undefined) {
                winningScatters.push(scatterSymbol);
            }

            if (
                winningScatters.length === minScattersToTriggerNearWin &&
                !(
                    reelId === reelsConfig.reelsNumber - 1 &&
                    totalScattersStoppedOnLanding <= minScattersToTriggerNearWin
                )
            ) {
                scatterState = ScatterStates.ANTICIPATION;
                EventsManager.trigger(SHAKE_NORMAL_SCREEN, 6);
            }
        }

        if (
            totalReelsLanded === reelsConfig.reelsNumber &&
            winningScatters.length <= minScattersToTriggerNearWin
        ) {
            if (winningScatters.length === minScattersToTriggerNearWin) {
                scatterState = ScatterStates.LOSE;
            } else {
                scatterState = ScatterStates.NORMAL;
            }
        } else if (winningScatters.length >= 3 && totalReelsLanded === reelsConfig.reelsNumber) {
            scatterState = ScatterStates.WIN;
        }

        if (wasFeatureBuy && !Apocalypse.model.isLuckyDraw) {
            if (scatterState === ScatterStates.WIN) {
                if (totalReelsLanded === reelsConfig.reelsNumber) {
                    const winningScattersTimeline: gsap.core.Timeline = gsap.timeline({
                        autoRemoveChildren: true,
                        smoothChildTiming: true,
                        paused: true,
                    });

                    winningScatters.forEach((scatter) => {
                        winningScattersTimeline.add(scatter.getWinAnimation(scatterState), 0);
                        // scatter.getWinAnimation(scatterState);
                    });
                    winningScattersTimeline.resume();
                }
            }
        } else {
            winningScatters.forEach((scatter) => {
                reelStopTimeline.add(
                    scatter.getWinAnimation(
                        scatterState,
                        scatterState === ScatterStates.ANTICIPATION &&
                            winningScatters.length > minScattersToTriggerNearWin &&
                            winningScatters.indexOf(scatter) > 1
                    ),
                    0
                );
            });
        }

        reelStopTimeline.resume();
        //////////////////
    };

    gameReels.forEach((reel, reelIndex) => {
        const stopAnimationTimeline = gsap.timeline({});

        let hasScatter: boolean = false;
        finalDataReels[reelIndex].forEach((symbolName: string) => {
            if (symbolName === scatterSymbolName) {
                hasScatter = true;
            }
        });

        const data: IReelData = {
            hasNearWin: false,
            hasScatter: hasScatter,
            stopSymbolNames: finalDataReels[reelIndex],
            firstScatterReelToStop: false,
        };
        const reelLabel: string = `reelBeginStop${reelIndex}`;

        data.firstScatterReelToStop = hasScatter && firstNearWinToStop;
        if (hasScatter) {
            firstNearWinToStop = false;
        }

        if (nearWinOnReels[reelIndex]) {
            timeline.addLabel(`${TimelineLabels.SHOW_NEAR_WIN_ANIMATION}${reelIndex}`, `>`);

            const reelNearWinAnimation = (reel as ApocalypseReel).getShowNearWinAnimation(
                reelIndex === 3 && hasSXNudgeNearWin,
                sxnPosition
            );

            timeline.add(
                reelNearWinAnimation,
                `${TimelineLabels.SHOW_NEAR_WIN_ANIMATION}${reelIndex}`
            );

            timeline.addLabel(
                reelLabel,
                `${TimelineLabels.SHOW_NEAR_WIN_ANIMATION}${reelIndex}+=${nearWinDuration}`
            );

            const stopReelAnimation = (reel as ApocalypseReel).stopNormalBetAnimation(
                data,
                onReelSpinComplete,
                onReelLand
            );

            stopAnimationTimeline.add(stopReelAnimation, 0);

            timeline.add(stopAnimationTimeline, reelLabel);

            timeline.addLabel(`reelStopped${reelIndex}`);
            timeline.addLabel(
                `${TimelineLabels.HIDE_NEAR_WIN_ANIMATION}${reelIndex}`,
                `reelStopped${reelIndex}`
            );

            timeline.add(
                reel.getHideNearWinAnimation(),
                `${TimelineLabels.HIDE_NEAR_WIN_ANIMATION}${reelIndex}`
            );
        } else {
            if (reelIndex > 0) {
                if (nearWinOnReels[reelIndex - 1]) {
                    timeline.addLabel(reelLabel, `>`);
                } else {
                    timeline.addLabel(reelLabel, `reelBeginStop${reelIndex - 1}+=${stopSpinDelay}`);
                }
            } else {
                timeline.addLabel(reelLabel, `>`);
            }

            stopAnimationTimeline.add(
                (reel as ApocalypseReel).stopNormalBetAnimation(
                    data,
                    onReelSpinComplete,
                    onReelLand
                ),
                0
            );

            timeline.add(stopAnimationTimeline, reelLabel);

            timeline.addLabel(`reelStopped${reelIndex}`, `>`);
        }
    });

    gameReels.forEach((reel, reelIndex) => {
        let hasScatter: boolean = false;
        finalDataReels[reelIndex].forEach((symbolName: string) => {
            if (symbolName === scatterSymbolName) {
                hasScatter = true;
            }
        });

        if (hasScatter) {
            const openBlockerLabel: string = `openBlocker${reelIndex}`;
            timeline.addLabel(openBlockerLabel, `reelStopped${reelIndex}`);
        }
    });

    // const entries = Object.entries(timeline.labels);
    //
    // const salam = entries.sort((a, b) => {
    //     return timeline.labels[a[0]] - timeline.labels[b[0]];
    // });
    //
    // console.log(salam);

    timeline.addLabel(TimelineLabels.SCATTER_DECORATOR_COMPLETE);
};
